import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATS = ["maintenance", "utility", "salary", "supplies", "other"];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    property_id: "", category: "maintenance", amount: 0,
    description: "", date: new Date().toISOString().slice(0, 10),
  });

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/expenses"), api.get("/properties")]);
    setItems(a.data); setProps(b.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!payload.property_id) delete payload.property_id;
      await api.post("/expenses", payload);
      toast.success("Expense added");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete expense?")) return;
    await api.delete(`/expenses/${id}`); load();
  };

  const propName = (id) => props.find((p) => p.id === id)?.name || "All properties";
  const total = items.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Track</div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter">Expenses</h1>
          <p className="text-sm text-slate-500 mt-2">Total spend: <span className="font-mono font-semibold text-slate-900">₹{total.toLocaleString("en-IN")}</span></p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-expense-btn"
                className="bg-blue-600 text-white px-5 py-3 hover:bg-blue-700 inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add expense
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 grid md:grid-cols-3 gap-4" data-testid="expense-form">
          <select className="border border-slate-300 px-4 py-2.5" value={form.property_id}
                  onChange={(e) => setForm({ ...form, property_id: e.target.value })}>
            <option value="">All properties (optional)</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="border border-slate-300 px-4 py-2.5" value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="exp-cat">
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input required type="number" min="0" placeholder="Amount ₹" data-testid="exp-amount"
                 className="border border-slate-300 px-4 py-2.5"
                 value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <input required placeholder="Description" data-testid="exp-desc"
                 className="border border-slate-300 px-4 py-2.5 md:col-span-2"
                 value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <input required type="date" className="border border-slate-300 px-4 py-2.5"
                 value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <div className="md:col-span-3 flex gap-3">
            <button data-testid="exp-save" className="bg-slate-900 text-white px-6 py-2.5">Save</button>
            <button type="button" onClick={() => setOpen(false)} className="bg-slate-100 px-6 py-2.5">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200">
        {items.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="expenses-empty">No expenses logged yet.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-6 py-3">Date</th><th className="px-6 py-3">Property</th>
                <th className="px-6 py-3">Category</th><th className="px-6 py-3">Description</th>
                <th className="px-6 py-3">Amount</th><th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-4 font-mono text-sm">{e.date}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{propName(e.property_id)}</td>
                  <td className="px-6 py-4"><span className="bg-slate-100 px-2 py-0.5 text-xs uppercase tracking-wider">{e.category}</span></td>
                  <td className="px-6 py-4 text-sm">{e.description}</td>
                  <td className="px-6 py-4 font-mono">₹{Number(e.amount).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => remove(e.id)} className="text-red-600 hover:bg-red-50 p-2"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
